import { useState, useEffect, useCallback } from 'react';
import FgService, { FgData } from '../services/fgService';

interface UseFgResult {
  isLoading: boolean;
  fgData: FgData | null;
  isCountryAllowed: boolean;
  isBotBypassed: boolean;
  error: string | null;
  performFg: () => Promise<void>;
  forceFg: () => Promise<void>;
  clearCache: () => void;
}

interface UseFgOptions {
  endpoint: string;
  allowedCountries?: string[];
  allowedBots?: string[];
  autoExecute?: boolean;
  sendDataEnabled?: boolean;
  bypassBots?: boolean;
  onBlocked?: (countryCode: string) => void;
  onSuccess?: (fgData: FgData) => void;
  onBotBypassed?: (botName: string | null) => void;
}

export const useFg = (options: UseFgOptions): UseFgResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [fgData, setFgData] = useState<FgData | null>(null);
  const [isCountryAllowed, setIsCountryAllowed] = useState(true);
  const [isBotBypassed, setIsBotBypassed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fgService = new FgService(
    options.endpoint, 
    options.sendDataEnabled ?? true,
    options.allowedBots ?? [],
    options.bypassBots ?? true
  );
  // Configura i paesi consentiti se forniti
  useEffect(() => {
    if (options.allowedCountries) {
      fgService.setAllowedCountries(options.allowedCountries);
    }
    if (options.allowedBots) {
      fgService.setAllowedBots(options.allowedBots);
    }
    if (options.bypassBots !== undefined) {
      fgService.setBypassBots(options.bypassBots);
    }
  }, [options.allowedCountries, options.allowedBots, options.bypassBots, options.sendDataEnabled]);
  const performFg = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await fgService.performFg();
      
      setFgData(result.fgData);
      setIsCountryAllowed(result.allowed);
      setIsBotBypassed(result.isBotBypassed || false);

      if (result.isBotBypassed) {
        options.onBotBypassed?.(result.fgData.botName || null);
      } else if (!result.allowed && result.fgData.countryCode) {
        options.onBlocked?.(result.fgData.countryCode);
      }

      if (result.success) {
        options.onSuccess?.(result.fgData);
      } else {
        setError('Errore nell\'invio dei dati FG');
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Errore sconosciuto durante FG';
      setError(errorMessage);
      console.error('Errore in FG:', err);
    } finally {
      setIsLoading(false);
    }
  }, [options.endpoint, options.allowedCountries, options.sendDataEnabled]);

  // Esegui automaticamente FG se richiesto
  useEffect(() => {
    if (options.autoExecute) {
      performFg();
    }
  }, [options.autoExecute, performFg]);
  const forceFg = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await fgService.forceFg();
      
      setFgData(result.fgData);
      setIsCountryAllowed(result.allowed);
      setIsBotBypassed(result.isBotBypassed || false);

      if (result.isBotBypassed) {
        options.onBotBypassed?.(result.fgData.botName || null);
      } else if (!result.allowed && result.fgData.countryCode) {
        options.onBlocked?.(result.fgData.countryCode);
      }

      if (result.success) {
        options.onSuccess?.(result.fgData);
      } else {
        setError('Errore nell\'invio dei dati FG');
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Errore sconosciuto durante FG';
      setError(errorMessage);
      console.error('Errore in FG:', err);
    } finally {
      setIsLoading(false);
    }
  }, [options.endpoint, options.allowedCountries, options.sendDataEnabled]);

  const clearCache = useCallback(() => {
    fgService.clearCache();
    setFgData(null);
    setIsCountryAllowed(true);
    setIsBotBypassed(false);
    setError(null);
  }, []);

  return {
    isLoading,
    fgData,
    isCountryAllowed,
    isBotBypassed,
    error,
    performFg,
    forceFg,
    clearCache
  };
};

export default useFg;
